import type { LucideIcon } from "lucide-react";
import {
	CalendarCheckIcon,
	CalendarClockIcon,
	ClockIcon,
	ExternalLinkIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { buttonVariants } from "@/components/ui/button";
import {
	Card,
	CardAction,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { ScheduledMeetingResult } from "@/modules/threads/messageHelpers";

interface ScheduledMeetingCardProps {
	payload: ScheduledMeetingResult;
}

interface ScheduleDisplay {
	dateLabel: string;
	timeLabel: string;
}

const ICON_CLASS = "bg-primary/10 text-primary ring-primary/20";

/**
 * Split start/end into date and time labels. Falls back to raw ISO strings if
 * either side fails to parse, so a bad payload doesn't blank the card.
 */
function parseSchedule(startAt: string, endAt: string): ScheduleDisplay {
	const start = new Date(startAt);
	const end = new Date(endAt);
	if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
		return { dateLabel: startAt, timeLabel: endAt };
	}

	const sameDay =
		start.getFullYear() === end.getFullYear() &&
		start.getMonth() === end.getMonth() &&
		start.getDate() === end.getDate();

	const dateFmt = new Intl.DateTimeFormat(undefined, {
		weekday: "short",
		month: "short",
		day: "numeric",
		year: "numeric",
	});
	const timeFmt = new Intl.DateTimeFormat(undefined, {
		hour: "numeric",
		minute: "2-digit",
	});

	if (sameDay) {
		return {
			dateLabel: dateFmt.format(start),
			timeLabel: `${timeFmt.format(start)} – ${timeFmt.format(end)}`,
		};
	}

	return {
		dateLabel: `${dateFmt.format(start)} → ${dateFmt.format(end)}`,
		timeLabel: `${timeFmt.format(start)} → ${timeFmt.format(end)}`,
	};
}

function FieldStat({
	icon: Icon,
	label,
	value,
}: {
	icon: LucideIcon;
	label: string;
	value: string;
}) {
	return (
		<div className="flex min-w-0 items-start gap-2 rounded-lg bg-muted/40 px-3 py-2.5">
			<Icon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
			<div className="min-w-0">
				<div className="text-[0.68rem] font-medium text-muted-foreground uppercase">
					{label}
				</div>
				<div className="truncate text-sm font-medium tabular-nums">{value}</div>
			</div>
		</div>
	);
}

export function ScheduledMeetingCard({ payload }: ScheduledMeetingCardProps) {
	const schedule = parseSchedule(payload.startAt, payload.endAt);

	return (
		<Card className="my-3 w-full max-w-[42rem] border border-border" size="sm">
			<CardHeader className="gap-3">
				<div className="flex min-w-0 items-start gap-3">
					<div
						className={cn(
							"flex size-9 shrink-0 items-center justify-center rounded-lg ring-1",
							ICON_CLASS,
						)}
					>
						<CalendarCheckIcon className="size-4" />
					</div>
					<div className="min-w-0 flex-1">
						<CardTitle className="truncate">{payload.summary}</CardTitle>
						<CardDescription className="mt-1">
							Added to your Clio calendar
						</CardDescription>
					</div>
				</div>
				<CardAction>
					<Badge variant="secondary">Clio</Badge>
				</CardAction>
			</CardHeader>

			<CardContent className="flex flex-col gap-3">
				<div className="grid gap-2 sm:grid-cols-2">
					<FieldStat
						icon={CalendarClockIcon}
						label="Date"
						value={schedule.dateLabel}
					/>
					<FieldStat icon={ClockIcon} label="Time" value={schedule.timeLabel} />
				</div>
			</CardContent>

			<CardFooter className="flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="min-w-0">
					{payload.matterTitle ? (
						<Badge variant="outline">Matter {payload.matterTitle}</Badge>
					) : (
						<span className="text-xs text-muted-foreground">
							No matter linked
						</span>
					)}
				</div>
				{payload.eventUrl ? (
					<a
						href={payload.eventUrl}
						target="_blank"
						rel="noreferrer"
						className={cn(
							buttonVariants({ variant: "outline", size: "sm" }),
							"w-full sm:w-auto",
						)}
					>
						Open in Clio
						<ExternalLinkIcon data-icon="inline-end" />
					</a>
				) : null}
			</CardFooter>
		</Card>
	);
}
